import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import api from '../lib/api';
import { ArrowLeft, LayoutGrid, Camera, Check, Star, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

interface Planogram {
  id: string;
  name: string;
  category_name: string;
  image_url: string;
}

export default function PlanogramCheck({ visitId, storeId }: { visitId: string; storeId: string }) {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [selectedPlanogram, setSelectedPlanogram] = useState<string | null>(null);
  const [compliance, setCompliance] = useState<number>(0);
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: planograms, isLoading } = useQuery<Planogram[]>({
    queryKey: ['planograms', storeId],
    queryFn: async () => {
      try {
        const response = await api.get(`/planograms?is_active=true&store_id=${storeId}`);
        return response.data.data;
      } catch {
        return [];
      }
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/visits/${visitId}/planogram-checks`, {
        planogram_id: selectedPlanogram,
        compliance_score: compliance,
        notes,
      });
    },
    onSuccess: () => {
      setSaved(true);
      queryClient.invalidateQueries({ queryKey: ['visit', visitId] });
      setTimeout(() => setLocation(`/visit/${storeId}`), 1500);
    },
  });

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <header className="bg-primary text-white p-4">
        <div className="flex items-center gap-4">
          <button onClick={() => setLocation(`/visit/${storeId}`)} className="p-2 -ml-2 rounded-full hover:bg-white/20">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Planograma</h1>
            <p className="text-sm text-white/80">Verificar conformidade</p>
          </div>
        </div>
      </header>

      <main className="flex-1 p-4 space-y-4">
        {saved && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
            <Check className="h-5 w-5 text-green-600" />
            <span className="text-sm text-green-800 font-medium">Verificação salva!</span>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />
            ))}
          </div>
        ) : !planograms?.length ? (
          <div className="text-center py-12">
            <LayoutGrid className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum planograma cadastrado.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600">Selecione o planograma para verificar:</p>

            {planograms.map((planogram) => {
              const isSelected = selectedPlanogram === planogram.id;
              const isExpanded = expanded === planogram.id;

              return (
                <div key={planogram.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => {
                      if (planogram.id !== selectedPlanogram) {
                        setCompliance(0);
                        setNotes('');
                      }
                      setSelectedPlanogram(planogram.id);
                      setExpanded(expanded === planogram.id ? null : planogram.id);
                    }}
                    className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${
                      isSelected ? 'bg-primary/5 border-l-4 border-primary' : ''
                    }`}
                  >
                    <div className="p-2 rounded-lg bg-indigo-50">
                      <LayoutGrid className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{planogram.name}</p>
                      <p className="text-xs text-gray-500">{planogram.category_name}</p>
                    </div>
                    {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </button>

                  {isExpanded && (
                    <div className="p-4 border-t border-gray-100">
                      <p className="text-xs text-gray-500 mb-2 font-medium">Planograma de referência:</p>
                      <div className="bg-gray-100 rounded-lg overflow-hidden mb-4">
                        <img
                          src={planogram.image_url}
                          alt={planogram.name}
                          className="w-full h-48 object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>

                      <button
                        onClick={() => setLocation(`/camera/${visitId}?type=PLANOGRAM`)}
                        className="w-full flex items-center justify-center gap-2 py-3 bg-blue-50 text-blue-700 rounded-xl font-medium mb-4"
                      >
                        <Camera className="h-4 w-4" />
                        Tirar foto da gôndola
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            {selectedPlanogram && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500" />
                  Avaliação de Conformidade
                </h3>

                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Conformidade</span>
                    <span className="font-semibold">{compliance}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={compliance}
                    onChange={(e) => setCompliance(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Observações</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Descreva as diferenças encontradas..."
                    rows={3}
                    className="w-full rounded-xl border border-gray-200 p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </main>

      {selectedPlanogram && (
        <div className="p-4 bg-white border-t border-gray-200">
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || saved}
            className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-xl font-semibold disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
            {saveMutation.isPending ? 'Salvando...' : 'Salvar Avaliação'}
          </button>
        </div>
      )}
    </div>
  );
}
